import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

/**
 * Represents a configurable value that can be stored in the configuration system.
 */
export type ConfigValue = string | number | boolean | object | null;

/**
 * Interface defining methods for storing and retrieving configuration elements.
 */
interface _ConfigStorage {
    /**
     * Stores a complete configuration element.
     * @param element - The name of the element to store.
     * @param data - An object representing the element's configuration data.
     */    
    setElement(element: string, data: { [key: string]: ConfigValue }): void;

    /**
     * Retrieves a configuration element.
     * @param element - The name of the element to retrieve.
     * @returns An object containing the configuration data, or `null` if the element does not exist.
     */
    getElement(element: string): { [key: string]: ConfigValue } | null;
}

/**
 * Represents a single configuration element, allowing for data manipulation and persistence.
 */
export class Configuration {
    private data: { [key: string]: ConfigValue } = {};
    private _dirty = false;

    private _onUpdated = new Subject <void> ();

    /**
     * An observable that emits whenever the configuration is updated.
     * Consumers can subscribe to react to updates.
     */    
    public OnUpdated = this._onUpdated.asObservable();

    /**
     * Loads the existing data for the configuration element from storage.
     * If no data exists, initializes an empty configuration.
     * @private
     */    
    private load(): void {
        const elementData = this.config.getElement(this.element);
        if (elementData) {
            this.data = elementData;
        }
    }
  
    /**
     * Initializes the `Configuration` instance and loads existing data for the element.
     * @param element - The name of the configuration element.
     * @param config - The storage service to use for persisting data.
     */    
    constructor(private element: string, private config: _ConfigStorage){
        this.load();
    }
  
    /**
     * Indicates whether changes to the configuration are saved automatically.
     */
    private autocommit = true;

    /**
     * Gets the `autoCommit` status.
     * @returns `true` if changes are saved automatically, otherwise `false`.
     */
    get autoCommit(): boolean{
        return this.autocommit;
    }

    /**
     * Sets the `autoCommit` status.
     * @param value - `true` to enable automatic saving of changes, `false` otherwise.
     */    
    set autoCommit(value: boolean) {
        this.autocommit = value;
    }
  
    /**
     * Retrieves the value of a specific configuration key.
     * @param item - The name of the configuration key.
     * @returns The value associated with the key, or `undefined` if it does not exist.
     */    
    get(item: string): ConfigValue {
        return this.data[item];
    }

    /**
     * Sets the value of a specific configuration key.
     * If the value is `null`, the key is removed from the configuration.
     * @param item - The name of the configuration key.
     * @param value - The value to assign to the key, or `null` to remove it.
     */    
    set(item: string, value: ConfigValue): void {
        if (value == null){
            if (item in this.data){
                this._dirty = true;
                delete this.data[item];
            }
        }
        else {
            if (this.data[item] != value){
                this.data[item] = value;
                this._dirty = true;
            }
        }

        if (this._dirty && this.autoCommit) {
            this.commit();
        }
    }
  
    /**
     * Saves all current configuration data to storage.
     */    
    commit(): void {
        if (this._dirty){
            this.config.setElement(this.element, this.data);
            this._dirty = false;
            this._onUpdated.next();
        }
    }
}

/**
 * Service for managing configuration elements, providing methods for
 * creating and interacting with `Configuration` instances.
 */
@Injectable({
    providedIn: 'root',
})
export class configService implements _ConfigStorage {
    private prefix = 'upp_cfg_';
    private _served = new Map <string, Configuration> ();

    /**
     * Retrieves a `Configuration` instance for a specific element.
     * @param element - The name of the configuration element.
     * @returns A `Configuration` instance for managing the element.
     */    
    getConfiguration(element: string): Configuration {
        let _served = this._served.get(element);
        if (!_served){
            _served = new Configuration(element, this);
            this._served.set(element, _served);
        }
        return _served
    }

    /**
     * Stores a complete configuration element in `localStorage`.
     * @param element - The name of the configuration element.
     * @param data - An object representing the element's configuration data.
     */    
    setElement(element: string, data: { [key: string]: ConfigValue }): void {
        const key = this.prefix + element;
        try {
            const serializedData = JSON.stringify(data);
            localStorage.setItem(key, serializedData);
        } 
        catch (error) {
            console.error(`[CONFIGURATION] Could not save element: "${element}":`, error);
        }
    }

    /**
     * Retrieves a configuration element from `localStorage`.
     * @param element - The name of the configuration element.
     * @returns An object containing the configuration data, or `null` if the element does not exist.
     */    
    getElement(element: string): { [key: string]: ConfigValue } | null {
        const key = this.prefix + element;
        try {
            const serializedData = localStorage.getItem(key);
            const parsedData = serializedData ? JSON.parse(serializedData) : null;
            return typeof parsedData === 'object' && parsedData !== null ? parsedData : null;
        } 
        catch (error) {
            console.error(`[CONFIGURATION] Could not retrieve element: "${element}":`, error);
            return null;
        }
    }
}